import { FC, useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  ArrowRight,
  Bell,
  CalendarDays,
  Flag,
  Gauge,
  Gift,
  HeartPulse,
  LucideIcon,
  ShieldCheck,
  Sparkles,
  Trophy,
  Users,
} from "lucide-react";

import { clsxMerge } from "shared/lib/helpers";
import { LucideIconWithGradient } from "shared/ui/icons/lucide";
import { Button } from "shared/ui/shadcn/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "shared/ui/shadcn/dialog";

import {
  WHATS_NEW_ICONS,
  WhatsNewCardI,
  WhatsNewIconT,
  WhatsNewSectionI,
} from "./lib/types";

/**
 * The "What's new" dialog (C-16 S0): one card at a time, newest first, with
 * a pager when several are live (C16-D315). Closing it any way (Got it, ✕,
 * Esc, outside, or one of its links) dismisses every card it holds for today.
 * Brand gradient on the accents only (design system §7.4).
 */

const BRAND_START = "#4B61AB";
const BRAND_STOP = "#CC3997";

const ICONS: Record<WhatsNewIconT, LucideIcon> = {
  [WHATS_NEW_ICONS.CALENDAR]: CalendarDays,
  [WHATS_NEW_ICONS.HEALTH]: HeartPulse,
  [WHATS_NEW_ICONS.STRIKE]: Flag,
  [WHATS_NEW_ICONS.SCORE]: Gauge,
  [WHATS_NEW_ICONS.TEAM]: Users,
  [WHATS_NEW_ICONS.BELL]: Bell,
  [WHATS_NEW_ICONS.TROPHY]: Trophy,
  [WHATS_NEW_ICONS.SHIELD]: ShieldCheck,
  [WHATS_NEW_ICONS.GIFT]: Gift,
  [WHATS_NEW_ICONS.SPARKLES]: Sparkles,
};

const SectionIcon: FC<{ icon: WhatsNewIconT }> = ({ icon }) => (
  <span
    aria-hidden
    className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-black/[0.06] bg-black/[0.03]"
  >
    <LucideIconWithGradient
      icon={ICONS[icon] ?? Sparkles}
      startColor={BRAND_START}
      stopColor={BRAND_STOP}
      size={18}
      strokeWidth={2}
    />
  </span>
);

const Section: FC<{
  section: WhatsNewSectionI;
  onFollowLink: () => void;
}> = ({ section, onFollowLink }) => {
  const titleId = `whats-new-section-${section.id}`;

  return (
    <section aria-labelledby={titleId} className="flex gap-3.5 py-4">
      <SectionIcon icon={section.icon} />

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <h3 id={titleId} className="pt-0.5 typography-body-4-semibold">
          {section.title}
        </h3>

        <ul className="flex flex-col gap-1">
          {section.items.map((item) => (
            <li
              key={item}
              className="relative pl-3.5 text-black/70 typography-body-4"
            >
              <span
                aria-hidden
                className="absolute left-0 top-[0.6em] size-1 rounded-full bg-black/30"
              />
              {item}
            </li>
          ))}
        </ul>

        {section.link && (
          <Link
            href={section.link.href}
            onClick={onFollowLink}
            className="group mt-0.5 inline-flex w-fit items-center gap-1 rounded-sm text-black typography-body-5-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {section.link.label}
            <ArrowRight
              aria-hidden
              className="size-3.5 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none"
            />
          </Link>
        )}
      </div>
    </section>
  );
};

const Pager: FC<{ count: number; index: number }> = ({ count, index }) => (
  <div className="flex items-center gap-2">
    <div aria-hidden className="flex items-center gap-1">
      {Array.from({ length: count }, (_, dot) => (
        <span
          key={dot}
          className={clsxMerge(
            "h-1.5 rounded-full transition-all duration-300 motion-reduce:transition-none",
            dot === index ? "app-gradient w-4" : "w-1.5 bg-black/15"
          )}
        />
      ))}
    </div>

    <span className="tabular-nums text-black/60 typography-body-5">
      {index + 1} of {count}
    </span>
  </div>
);

export interface WhatsNewDialogPropsI {
  cards: Array<WhatsNewCardI>;
  isOpen: boolean;
  /** The viewer closed the dialog: dismiss every card for today. */
  onDismiss: () => void;
}

export const WhatsNewDialog: FC<WhatsNewDialogPropsI> = ({
  cards,
  isOpen,
  onDismiss,
}) => {
  const [index, setIndex] = useState(0);
  const primaryRef = useRef<HTMLButtonElement | null>(null);
  // A state ref: the body mounts in a portal after the first render.
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const count = cards.length;
  const card = cards[Math.min(index, count - 1)];
  const isLast = index >= count - 1;

  const updateMoreBelow = useCallback(() => {
    if (scroller) {
      setHasMoreBelow(
        scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight > 8
      );
    }
  }, [scroller]);

  // A new page starts at its top; the fade shows while more is below.
  useEffect(() => {
    if (!scroller) {
      return;
    }

    scroller.scrollTo?.({ top: 0 });
    updateMoreBelow();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(updateMoreBelow);

    observer.observe(scroller);

    return () => observer.disconnect();
  }, [index, scroller, updateMoreBelow]);

  if (!card) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onDismiss();
        }
      }}
    >
      <DialogContent
        onOpenAutoFocus={(event) => {
          // Land on the primary action, not on the first link in the body.
          event.preventDefault();
          primaryRef.current?.focus();
        }}
        // Only an explicit close dismisses it (Got it, ✕, Esc, a link): a
        // stray click outside, or the tab losing focus while it opens in the
        // background, would otherwise hide it for the whole day unread.
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
        className="flex max-h-[min(720px,calc(100vh-48px))] w-[calc(100vw-32px)] max-w-[600px] flex-col gap-0 overflow-hidden rounded-[20px] border-black/[0.08] p-0"
      >
        <span aria-hidden className="app-gradient h-[3px] w-full shrink-0" />

        <header className="flex flex-col gap-2 px-6 pb-1 pr-12 pt-[21px]">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-[#CC3997]/20 bg-white px-2.5 py-1">
            <LucideIconWithGradient
              aria-hidden
              icon={Sparkles}
              startColor={BRAND_START}
              stopColor={BRAND_STOP}
              size={14}
              strokeWidth={2}
            />
            <span className="text-gradient typography-body-5-semibold">
              What&apos;s new
            </span>
          </span>

          <DialogTitle className="typography-header-7-semibold">
            {card.title}
          </DialogTitle>

          {card.intro ? (
            <DialogDescription className="text-black/70 typography-body-4">
              {card.intro}
            </DialogDescription>
          ) : (
            <DialogDescription className="sr-only">
              What changed in the app, and what is new for you.
            </DialogDescription>
          )}
        </header>

        <div
          ref={setScroller}
          onScroll={updateMoreBelow}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6"
        >
          <div className="divide-y divide-black/[0.06]">
            {card.sections.map((section) => (
              <Section
                key={section.id}
                section={section}
                onFollowLink={onDismiss}
              />
            ))}
          </div>

          <div
            aria-hidden
            data-testid="whats-new-more-below"
            data-visible={hasMoreBelow}
            className={clsxMerge(
              "pointer-events-none sticky bottom-0 -mt-12 h-12 bg-gradient-to-t from-white to-white/0 transition-opacity duration-200 motion-reduce:transition-none",
              hasMoreBelow ? "opacity-100" : "opacity-0"
            )}
          />
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-black/[0.06] px-6 py-4">
          {count > 1 ? <Pager count={count} index={index} /> : <span />}

          <div className="flex items-center gap-2">
            {index > 0 && (
              <Button
                variant="ghost"
                onClick={() => setIndex((page) => Math.max(0, page - 1))}
                className="focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                Back
              </Button>
            )}

            {/* One button for Next and Got it, so focus stays on it. */}
            <Button
              ref={primaryRef}
              onClick={
                isLast
                  ? onDismiss
                  : () => setIndex((page) => Math.min(count - 1, page + 1))
              }
              className="min-w-[96px] focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {isLast ? "Got it" : "Next"}
            </Button>
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  );
};
